import type {
  AggregatedPortfolioSettings,
  CombinePortfoliosOrganizeOptionEnum,
  CombinePortfoliosSettings,
  CombinePortfoliosStructureOptionEnum,
  Portfolio,
  PortfolioInformation,
} from 'venn-api';
import { compact, groupBy } from 'lodash';
import { assertExhaustive } from '../type';
import { mergePortfolios } from './mergePortfolios';

const groupByField = (
  field: 'householdName' | 'clientName',
  portfolioInformation: PortfolioInformation[],
  portfolios: Portfolio[],
): Portfolio[] => {
  const grouped = groupBy(portfolioInformation, field);
  return Object.keys(grouped)
    .map((fieldName) => ({
      name: fieldName === 'undefined' || fieldName === 'null' ? 'Unknown' : fieldName,
      children: compact(grouped[fieldName]?.map((info) => portfolios.find((p) => p.id === info.id))),
    }))
    .filter((portfolio) => portfolio.children.length > 0) as Portfolio[];
};

const groupByFieldAtLevel = (
  field: 'householdName' | 'clientName',
  portfolioInformation: PortfolioInformation[],
  portfolios: Portfolio[],
  level: number,
): Portfolio[] => {
  if (level === 0) {
    return groupByField(field, portfolioInformation, portfolios);
  }
  return portfolios.map((portfolio) => ({
    ...portfolio,
    children: groupByFieldAtLevel(field, portfolioInformation, portfolio.children ?? [], level - 1),
  }));
};

const organizeBy = (organizeOption: CombinePortfoliosOrganizeOptionEnum | undefined, portfolios: Portfolio[]) => {
  switch (organizeOption) {
    case undefined:
    case 'PORTFOLIO':
      return portfolios;
    case 'ASSET_CLASS':
      return portfolios.map((group) => ({
        ...group,
        children: mergePortfolios(group.children),
      }));
    default:
      throw assertExhaustive(organizeOption);
  }
};

const groupPortfolios = (
  portfolios: Portfolio[],
  portfolioInformation: PortfolioInformation[],
  combinedPortfolioName: string,
  structureOption: CombinePortfoliosStructureOptionEnum,
): Portfolio[] => {
  switch (structureOption) {
    case 'COMBINED':
      return [
        {
          name: combinedPortfolioName,
          children: portfolios,
          allocation: portfolios.reduce((sum, p) => sum + (p.allocation ?? 0), 0),
        } as Portfolio,
      ];
    case 'SEPARATE':
      return portfolios;
    case 'HOUSEHOLD':
      return groupByField('householdName', portfolioInformation, portfolios);
    case 'CLIENT':
      return groupByField('clientName', portfolioInformation, portfolios);
    default:
      throw assertExhaustive(structureOption);
  }
};

/**
 * Deterministic id for an aggregated-portfolio subject (venn-state), which has no scalar id of its own.
 * Lives here rather than in venn-state because venn-state depends on venn-utils at runtime (e.g. CircularBuffer);
 * the reverse edge would create a circular require. Ids and combineSettings keys are order-normalized so
 * equivalent settings always produce the same key.
 */
export const aggregatedSubjectKey = ({ portfolioInformation, combineSettings }: AggregatedPortfolioSettings): string =>
  `aggregated#${[...portfolioInformation]
    .sort((a, b) => a.id - b.id)
    .map((p) => p.id)
    .join(',')}#${JSON.stringify(combineSettings, Object.keys(combineSettings).sort())}`;

export const combinePortfolios = (
  portfolios: Portfolio[],
  portfolioInformation: PortfolioInformation[],
  combineSettings: CombinePortfoliosSettings,
): Portfolio[] => {
  const { structureOption, organizeOption, combinedPortfolioName, includeHousehold, includeClient } = combineSettings;

  // ASSET_CLASS organization replaces nodes with asset-class merges that no longer carry the original
  // portfolio ids, so household/client grouping (which looks nodes up by those ids) would match nothing
  // and filter the whole tree to empty. The wizard's CombineSettingsStep already disables
  // include-household/include-client whenever organizeOption is ASSET_CLASS; mirror that rule here so an
  // out-of-band settings combination (e.g. a directly-created RCM aggregated saved view) can't produce an
  // empty, analysis-breaking portfolio.
  const householdGroupingApplies = includeHousehold && organizeOption !== 'ASSET_CLASS';
  const clientGroupingApplies = includeClient && organizeOption !== 'ASSET_CLASS';

  let outputPortfolios = groupPortfolios(portfolios, portfolioInformation, combinedPortfolioName, structureOption);
  outputPortfolios = organizeBy(organizeOption, outputPortfolios);

  let depth = structureOption === 'SEPARATE' ? 0 : 1;
  if (householdGroupingApplies) {
    outputPortfolios = groupByFieldAtLevel('householdName', portfolioInformation, outputPortfolios, depth);
    depth += 1;
  }
  if (clientGroupingApplies) {
    outputPortfolios = groupByFieldAtLevel('clientName', portfolioInformation, outputPortfolios, depth);
  }

  return outputPortfolios;
};
